import type { AddressBookEntry, AddressEditorState } from './addressBookHelpers'
import { createAddressId, toAddressSignature } from './addressBookHelpers'

export const toEditorStateFromAddress = (entry: AddressBookEntry): AddressEditorState => ({
  label: entry.label,
  firstName: entry.firstName,
  lastName: entry.lastName,
  addressLine: entry.addressLine,
  city: entry.city,
  postalCode: entry.postalCode,
  province: entry.province,
  country: entry.country,
  phone: entry.phone,
  useShipping: entry.useShipping,
  useBilling: entry.useBilling,
  setDefault: false,
})

type BuildAddressCandidateInput = {
  editorState: AddressEditorState
  editingAddressId: string | null
  addressBook: AddressBookEntry[]
}

type BuildAddressCandidateResult =
  | {
      kind: 'ok'
      candidate: AddressBookEntry
    }
  | {
      kind: 'required_error' | 'duplicate_error'
    }

export const buildAddressCandidate = ({
  editorState,
  editingAddressId,
  addressBook,
}: BuildAddressCandidateInput): BuildAddressCandidateResult => {
  const firstName = editorState.firstName.trim()
  const lastName = editorState.lastName.trim()
  const addressLine = editorState.addressLine.trim()
  const city = editorState.city.trim()
  const postalCode = editorState.postalCode.trim()
  const province = editorState.province.trim()
  const country = editorState.country.trim()
  const phone = editorState.phone.trim()
  const label = editorState.label.trim()

  if (!firstName || !lastName || !addressLine || !city || !postalCode || !country) {
    return { kind: 'required_error' }
  }

  const candidate: AddressBookEntry = {
    id: editingAddressId ?? createAddressId(),
    label,
    firstName,
    lastName,
    addressLine,
    city,
    postalCode,
    province,
    country,
    phone,
    useShipping: editorState.useShipping,
    useBilling: editorState.useBilling,
    defaultShipping: false,
    defaultBilling: false,
  }

  const duplicate = addressBook.some(
    (entry) => entry.id !== candidate.id && toAddressSignature(entry) === toAddressSignature(candidate),
  )

  if (duplicate) {
    return { kind: 'duplicate_error' }
  }

  const current = addressBook.find((entry) => entry.id === candidate.id)
  const preserveShippingDefault = Boolean(current?.defaultShipping && candidate.useShipping)
  const preserveBillingDefault = Boolean(current?.defaultBilling && candidate.useBilling)

  candidate.defaultShipping = preserveShippingDefault
  candidate.defaultBilling = preserveBillingDefault

  if (editorState.setDefault) {
    if (candidate.useShipping) candidate.defaultShipping = true
    if (candidate.useBilling) candidate.defaultBilling = true
  }

  return {
    kind: 'ok',
    candidate,
  }
}

export const upsertAddressCandidate = ({
  addressBook,
  candidate,
  editingAddressId,
}: {
  addressBook: AddressBookEntry[]
  candidate: AddressBookEntry
  editingAddressId: string | null
}) => {
  let next = editingAddressId
    ? addressBook.map((entry) => (entry.id === editingAddressId ? candidate : entry))
    : [candidate, ...addressBook]

  if (candidate.defaultShipping) {
    next = next.map((entry) => (entry.id === candidate.id ? entry : { ...entry, defaultShipping: false }))
  }

  if (candidate.defaultBilling) {
    next = next.map((entry) => (entry.id === candidate.id ? entry : { ...entry, defaultBilling: false }))
  }

  return next
}

export const setAddressBookDefaultRole = ({
  addressBook,
  entryId,
  role,
}: {
  addressBook: AddressBookEntry[]
  entryId: string
  role: 'shipping' | 'billing'
}) =>
  addressBook.map((entry) => {
    if (entry.id !== entryId) {
      return {
        ...entry,
        defaultShipping: role === 'shipping' ? false : entry.defaultShipping,
        defaultBilling: role === 'billing' ? false : entry.defaultBilling,
      }
    }

    if (role === 'shipping') {
      return {
        ...entry,
        useShipping: true,
        defaultShipping: true,
      }
    }

    return {
      ...entry,
      useBilling: true,
      defaultBilling: true,
    }
  })

export const removeAddressBookEntry = (addressBook: AddressBookEntry[], entryId: string) =>
  addressBook.filter((entry) => entry.id !== entryId)
